import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material';
import { CompanyApi } from '@api/companies.api';
import { DeadlineCotractApi } from '@api/deadlineContract.api';
import { ProblemsApi } from '@api/problems.api';
import { SegmentApi } from '@api/segments.api';
import { MaintenancesApi } from '@core/api/maintenances/maintenances.api';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { Equipment, Maintenance, Segment } from '@core/interfaces';
import { DeadlineContract } from '@core/interfaces/deadlines-contracts';
import { WarrantyStatus } from '@core/interfaces/warranties';
import { mapId } from '@core/utils/filters-utils';
import { getErrorCode } from '@core/utils/get-error-code';
import { ConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { merge, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { sentry } from 'sentry';
import { Validation } from '../../../../core/services/address-validation/address-validation.service';
import { PreviousWarrantyDialogComponent } from './components/previous-warranty-dialog/previous-warranty-dialog.component';
import { DialogCreateMaintenancesService } from './dialog-create-maintenances.service';

@Component({
  selector: 'dialog-create-maintenances',
  templateUrl: './dialog-create-maintenances.component.html',
  styleUrls: ['./dialog-create-maintenances.component.scss'],
  providers: [DialogCreateMaintenancesService]
})
export class DialogCreateMaintenanceComponent implements OnDestroy, OnInit {
  public isLoading = false;
  public maintenanceForm: FormGroup;
  public problems = [];
  public selectedSegment: Segment;
  public validation: Validation;
  public hasCreateWarrantyPermission = this.authenticationService.hasPermission('warranties-create');
  public hasEditWarrantyPermission = this.authenticationService.hasPermission('warranties-edit');
  private alreadyShownPreviousWarrantyDialog = false;
  public firstView = true;

  private isProblemRequired = this.authenticationService.hasAccountPreference({
    property: 'openMaintenance',
    subProperty: 'featureProblemRequired',
    expectedValue: true,
    defaultIfUndefined: false
  });

  private maintenanceNumberGeneratorType = this.authenticationService.getAccountPreference({
    property: 'openMaintenance',
    subProperty: 'maintenanceNumberGenerator',
    defaultIfNull: 'sequential'
  });

  private readonly removeSubscriptions = new Subject<void>();

  constructor(
    public readonly matDialogRef: MatDialogRef<null>,
    private readonly segmentsApi: SegmentApi,
    private readonly problemsApi: ProblemsApi,
    private readonly companiesApi: CompanyApi,
    private readonly deadlineContractApi: DeadlineCotractApi,
    private readonly maintenancesApi: MaintenancesApi,
    private readonly progressBar: FuseProgressBarService,
    private readonly customMessageService: CustomMessagesService,
    private readonly authenticationService: AuthenticationService,
    private readonly dialogCreateMaintenanceService: DialogCreateMaintenancesService,
    private matDialog: MatDialog,
    private readonly translateService: TranslateService
  ) { }

  async ngOnInit(): Promise<void> {
    this.maintenanceForm = this.dialogCreateMaintenanceService.createMaintenanceForm({
      maintenanceNumberGeneratorType: this.maintenanceNumberGeneratorType
    });

    this.subscribeToSegment();
    this.subscribeToProblem();
    this.subscribeToCompany();
    this.subscribeToDeadlineContract();
    this.subscribeToLocationChanges();
    this.subscribeToWarranties();

    window.addEventListener('warrantiesUpdated', this.warrantiesUpdatedHandler);
    this.firstView = false;
  }

  public async shouldContinueWithPreviousWarrantyDialog(maintenances?: Maintenance[]): Promise<boolean> {
    const data = maintenances || null;

    const dialogRef = this.matDialog.open(PreviousWarrantyDialogComponent, {
      panelClass: ['previous-warranty-dialog'],
      width: '553px',
      height: 'auto',
      disableClose: true,
      data
    });

    const result = await dialogRef.afterClosed().toPromise();

    return result ? result.selectedMaintenanceId : null;
  }

  ngOnDestroy(): void {
    window.removeEventListener('warrantiesUpdated', this.warrantiesUpdatedHandler);
    window.emitEvent('clearMaintenanceWarrantiesVariables');
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  async saveMaintenance(): Promise<void> {
    const previousMaintenancesWarranties = await this.dialogCreateMaintenanceService.hasPreviousMaintenanceWarranties(this.maintenanceForm);

    if (previousMaintenancesWarranties) {
      const selectedMaintenanceId = await this.shouldContinueWithPreviousWarrantyDialog(previousMaintenancesWarranties);
      this.maintenanceForm.get('previousMaintenanceId').setValue(selectedMaintenanceId);
    }

    this.progressBar.show();
    this.isLoading = true;

    const attachmentsMapped = await this.dialogCreateMaintenanceService.toMaintenanceAttachmentsInput(this.maintenanceForm);
    const scheduleSuggestionsMapped = this.dialogCreateMaintenanceService.toMaintenanceScheduleSuggestionsInput(this.maintenanceForm);
    const maintenance = this.dialogCreateMaintenanceService.toMaintenanceCreateInput({
      form: this.maintenanceForm,
      attachmentsMapped,
      scheduleSuggestionsMapped
    });

    try {
      await this.maintenancesApi.save(maintenance);
      this.matDialogRef.close(true);
      this.customMessageService.success('MAINTENANCES.CREATE_DIALOG.SUCCESS_MESSAGE');
    } catch (err) {
      const code = getErrorCode(err);
      if (code === 'maintenance_number_already_exists') {
        this.maintenanceForm.get('number').setErrors({
          'maintenanceNumberAlreadyExists': true
        });

        return;
      }

      this.customMessageService.error('MAINTENANCES.CREATE_DIALOG.ERROR_MESSAGE');
      sentry.captureException(err);
    } finally {
      this.progressBar.hide();
      this.isLoading = false;
    }
  }

  private warrantiesUpdatedHandler = (event: CustomEvent) => {
    const { warranties } = event.detail;
    this.maintenanceForm.get('warranties').setValue(warranties);
  }

  private subscribeToSegment(): void {
    this.maintenanceForm.get('segment').valueChanges
      .subscribe(async (value) => {
        this.maintenanceForm.get('equipments').patchValue([]);
        this.maintenanceForm.get('locationSectors').patchValue([]);
        this.maintenanceForm.get('locationEnvironments').patchValue([]);

        if (!value) {
          this.maintenanceForm.get('maintenanceType').setValue(null);
          this.maintenanceForm.get('maintenanceType').disable();
          return;
        }

        this.maintenanceForm.get('maintenanceType').enable();
        this.selectedSegment = (await this.segmentsApi.searchOne(value.id));
      });
  }

  private subscribeToProblem(): void {
    merge(
      this.maintenanceForm.get('segment').valueChanges,
      this.maintenanceForm.get('maintenanceType').valueChanges
    ).pipe(
      takeUntil(this.removeSubscriptions),
      switchMap(async () => {
        const where = {};

        if (this.maintenanceForm.get('segment').value) {
          where['segmentId_in'] = mapId(this.maintenanceForm.get('segment').value);
        }

        if (this.maintenanceForm.get('maintenanceType').value) {
          where['maintenanceTypeId_in'] = mapId(this.maintenanceForm.get('maintenanceType').value);
        }

        return await this.problemsApi.searchProblems({
          where: {
            ...where,
            archived: false
          }
        });
      })
    ).subscribe((problems) => {

      if (!problems) {
        return;
      }

      this.problems = problems;

      if (!this.maintenanceForm.get('segment').value && !this.maintenanceForm.get('maintenanceType').value) {
        this.maintenanceForm.get('problem').setValue(null);
        this.maintenanceForm.get('problem').disable();
      } else {
        this.maintenanceForm.get('problem').enable();
      }

      const isProblemsOnList = this.problems.some((problem) => {
        return this.maintenanceForm.get('problem').value ? problem.id === this.maintenanceForm.get('problem').value.id : false;
      });

      if (this.maintenanceForm.get('problem').value && !isProblemsOnList) {
        this.maintenanceForm.get('problem').setValue(null);
      }

      if (problems.length && this.isProblemRequired) {
        this.maintenanceForm.get('problem').setValidators(Validators.required);
        this.maintenanceForm.get('problem').updateValueAndValidity();
      } else {
        this.maintenanceForm.get('problem').clearValidators();
        this.maintenanceForm.get('problem').updateValueAndValidity();
      }
    });
  }

  private subscribeToCompany(): void {
    merge(
      this.maintenanceForm.get('segment').valueChanges,
      this.maintenanceForm.get('maintenanceType').valueChanges,
      this.maintenanceForm.get('location').valueChanges
    )
    .pipe(
      takeUntil(this.removeSubscriptions),
      filter(() => {
        const hasSegment = Boolean(this.maintenanceForm.get('segment').value);
        const hasMaintenanceType = Boolean(this.maintenanceForm.get('maintenanceType').value);
        const hasLocation = Boolean(this.maintenanceForm.get('location').value);
        const hasNotCompany = Boolean(!this.maintenanceForm.get('company').value);

        return (
          hasSegment && hasMaintenanceType && hasLocation && hasNotCompany
        );
      }),
      switchMap(() => {
        return this.companiesApi.getCompanyByConfig({
          maintenanceTypeId: this.maintenanceForm.get('maintenanceType').value.id,
          segmentId: this.maintenanceForm.get('segment').value.id,
          locationId: this.maintenanceForm.get('location').value.id
        });
      })
      )
      .subscribe((company) => {
        this.maintenanceForm.get('company').setValue(company || null);
      });
  }

  private subscribeToDeadlineContract(): void {
    merge(
      this.maintenanceForm.get('segment').valueChanges,
      this.maintenanceForm.get('maintenanceType').valueChanges,
      this.maintenanceForm.get('location').valueChanges
    )
    .pipe(
      takeUntil(this.removeSubscriptions),
      filter(() => {
        const hasSegment = Boolean(this.maintenanceForm.get('segment').value);
        const hasMaintenanceType = Boolean(this.maintenanceForm.get('maintenanceType').value);
        const hasLocation = Boolean(this.maintenanceForm.get('location').value);

        return (
          hasSegment && hasMaintenanceType && hasLocation
        );
      }),
      switchMap(() => {
        return this.deadlineContractApi.getDeadlineContractByConfig({
          maintenanceTypeId: this.maintenanceForm.get('maintenanceType').value.id,
          segmentId: this.maintenanceForm.get('segment').value.id,
          locationId: this.maintenanceForm.get('location').value.id
        });
      })
    )
    .subscribe(async (receivedDeadlineContract: DeadlineContract | null) => {
      this.maintenanceForm.get('deadlineContract').enable();

      if (!receivedDeadlineContract) {
        return;
      }

      const currentDeadlineContract = this.maintenanceForm.get('deadlineContract').value;

      if (!currentDeadlineContract) {
        this.maintenanceForm.get('deadlineContract').setValue(receivedDeadlineContract);

        return;
      }

      const receivedDeadlineContractIsDifferentThanCurrent = currentDeadlineContract.id !== receivedDeadlineContract.id;

      if (receivedDeadlineContractIsDifferentThanCurrent) {
        const result = await this.matDialog.open(ConfirmDialogComponent, {
          data: {
            title: this.translateService.instant('MAINTENANCES.DEADLINE_CONTRACT_DIALOG.TITLE'),
            message: this.translateService.instant('MAINTENANCES.DEADLINE_CONTRACT_DIALOG.MESSAGE'),
            okText: this.translateService.instant('MAINTENANCES.DEADLINE_CONTRACT_DIALOG.OK_TEXT'),
            cancelText: this.translateService.instant('MAINTENANCES.DEADLINE_CONTRACT_DIALOG.CANCEL_TEXT')
          }
        })
          .afterClosed()
          .toPromise();

        if (!result || !result.confirmed) {
          return;
        }

        this.maintenanceForm.get('deadlineContract').setValue(receivedDeadlineContract);
        return;
      }
    });
  }

  private subscribeToLocationChanges(): void{
    this.maintenanceForm.get('location').valueChanges.pipe(
      takeUntil(this.removeSubscriptions))
      .subscribe(() => {
        this.maintenanceForm.get('equipments').setValue([]);
        this.maintenanceForm.get('locationSectors').setValue([]);
        this.maintenanceForm.get('locationEnvironments').setValue([]);


        if (this.maintenanceForm.get('location').value) {
          this.maintenanceForm.get('address').enable();
          this.maintenanceForm.get('company').enable();
          this.maintenanceForm.get('address').setValue(this.maintenanceForm.get('location').value.address);
          return;
        }
        this.maintenanceForm.get('address').setValue(null);
        this.maintenanceForm.get('address').disable();
        this.maintenanceForm.get('company').disable();
      });
  }

  private subscribeToWarranties(): void {
    this.maintenanceForm.get('equipments').valueChanges.pipe(
      takeUntil(this.removeSubscriptions))
      .subscribe((equipments) => {
        if (equipments.length) {
          const hasSomeEquipmentWithWarranty = equipments.some((equipment: Equipment) => {
            const warrantySummary = equipment.warrantySummary;
            const acceptedWarrantyStatus = [WarrantyStatus.active, WarrantyStatus.extended];
            return warrantySummary && warrantySummary.status && acceptedWarrantyStatus.includes(warrantySummary.status);
          });

          if (hasSomeEquipmentWithWarranty && !this.alreadyShownPreviousWarrantyDialog) {
            this.shouldContinueWithPreviousWarrantyDialog();
            this.alreadyShownPreviousWarrantyDialog = true;
          }
        }
      });
  }
}
